export type IrzEncryptedPassword = {
  v: 1;
  key_id: string;
  ek: string;
  iv: string;
  ct: string;
};

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const b64 = String(pem || "")
    .replace(/-----[^-]+-----/g, "")
    .replace(/\s+/g, "");
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    buf[i] = raw.charCodeAt(i);
  }
  return buf.buffer;
}

function bufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

type IrzPubKeyResponse = {
  detail?: string;
  public_key_pem?: string;
  key_id?: string;
};

async function fetchIrzPublicKey(): Promise<{ public_key_pem: string; key_id: string }> {
  const response = await fetch("/api/irz/session/pubkey", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  let data: IrzPubKeyResponse | null = null;
  try {
    data = (await response.json()) as IrzPubKeyResponse;
  } catch {
    data = null;
  }
  if (!response.ok) {
    throw new Error(
      data && typeof data.detail === "string"
        ? data.detail
        : "Nie udało się pobrać klucza szyfrowania IRZ.",
    );
  }
  if (!data?.public_key_pem || !data.key_id) {
    throw new Error("Nieprawidłowa odpowiedź klucza szyfrowania IRZ.");
  }
  return { public_key_pem: data.public_key_pem, key_id: data.key_id };
}

export async function encryptIrzPassword(password: string): Promise<IrzEncryptedPassword> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      "Przeglądarka nie obsługuje Web Crypto — wymagane HTTPS i nowoczesna przeglądarka.",
    );
  }
  if (!password) {
    throw new Error("Podaj hasło do IRZplus.");
  }

  const pub = await fetchIrzPublicKey();
  const publicKey = await globalThis.crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(pub.public_key_pem),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const aesKey = await globalThis.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"],
  );
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(password);
  const ciphertext = await globalThis.crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, encoded);
  const rawAes = await globalThis.crypto.subtle.exportKey("raw", aesKey);
  const wrapped = await globalThis.crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, rawAes);

  return {
    v: 1,
    key_id: pub.key_id,
    ek: bufferToBase64(wrapped),
    iv: bufferToBase64(iv),
    ct: bufferToBase64(ciphertext),
  };
}
